import React, { useMemo, useState } from "react";
import "./RenCostEstimator.css";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ArrowRight,
  Bookmark,
  Building2,
  Download,
  Gauge,
  Hammer,
  Home,
  Layers,
  Ruler,
  Target,
} from "lucide-react";
import {
  SimulatorHeader,
  ReuseCard,
  FieldWithIcon,
  FieldLabel,
} from "../components/SimulatorParts";
import useSimulatorFormat from "../components/useSimulatorFormat";
import { downloadSimulationWorkbook } from "../utils/simulationExcel";
import {
  asText,
  readSimulationWorkbook,
} from "../utils/simulationExcelImport";
import {
  BUILDING_TYPES,
  CURRENT_LABELS,
  ENERGY_MEASURES,
  NON_ENERGY_WORKS,
  RENOVATION_REFERENCE_FLANDERS,
  STOREYS,
  estimateBudgetToTarget,
  estimateCostPerMeasure,
  hasReferenceValues,
  isFloorAreaOutsidePilotRange,
  targetLabelsFor,
} from "./RenCostEstimatorFlandersCalc";

function MeasureChips({ options, selected, onToggle, t }) {
  return (
    <div className="ren-cost__chips">
      {options.map((option) => (
        <label
          key={option}
          className={`ren-cost__chip${selected.includes(option) ? " ren-cost__chip--active" : ""}`}
        >
          <input
            type="checkbox"
            checked={selected.includes(option)}
            onChange={() => onToggle(option)}
          />
          {t(`measures.${option}`)}
        </label>
      ))}
    </div>
  );
}

function MeasureRow({ row, range, wholeMoney, t }) {
  return (
    <div className="simulator__taxes-row">
      <span>
        {t(`measures.${row.measure}`)}
        <span className="simulator__row-detail">{range(row)}</span>
      </span>
      <span>{wholeMoney(row.typical)}</span>
    </div>
  );
}

export default function RenovationCostEstimatorFlanders({
  onBack,
  onContinue,
  reference = RENOVATION_REFERENCE_FLANDERS,
}) {
  const { t, i18n } = useTranslation("renovation-cost-estimator");
  const { wholeMoney, number } = useSimulatorFormat();

  const [floorArea, setFloorArea] = useState("");
  const [buildingType, setBuildingType] = useState("semi-detached");
  const [storeys, setStoreys] = useState(2);
  const [currentLabel, setCurrentLabel] = useState("E");
  const [targetLabel, setTargetLabel] = useState("B");
  const [measures, setMeasures] = useState([]);
  const [nonEnergyWorks, setNonEnergyWorks] = useState([]);

  const referenceReady = hasReferenceValues(reference);

  const changeCurrentLabel = (label) => {
    setCurrentLabel(label);
    const targets = targetLabelsFor(label);
    if (!targets.includes(targetLabel)) setTargetLabel(targets[targets.length - 1]);
  };

  const toggle = (setter) => (value) =>
    setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const budget = useMemo(
    () =>
      referenceReady
        ? estimateBudgetToTarget({ currentLabel, targetLabel, floorArea, reference })
        : null,
    [referenceReady, currentLabel, targetLabel, floorArea, reference]
  );

  const perMeasure = useMemo(
    () =>
      referenceReady
        ? estimateCostPerMeasure({
            floorArea,
            buildingType,
            storeys,
            measures,
            nonEnergyWorks,
            reference,
          })
        : null,
    [referenceReady, floorArea, buildingType, storeys, measures, nonEnergyWorks, reference]
  );

  const range = (row) => `${wholeMoney(row.low)} – ${wholeMoney(row.high)}`;

  const hasDownloadableResult =
    Boolean(budget || perMeasure);

  const handleDownload = async () => {
    if (!referenceReady) {
      return;
    }

    const currentInputs = {
      floorArea,
      buildingType,
      storeys,
      currentLabel,
      targetLabel,
      measures,
      nonEnergyWorks,
    };

    const currentBudget =
      estimateBudgetToTarget({
        currentLabel,
        targetLabel,
        floorArea,
        reference,
      });

    const currentPerMeasure =
      estimateCostPerMeasure({
        floorArea,
        buildingType,
        storeys,
        measures,
        nonEnergyWorks,
        reference,
      });

    if (
      !currentBudget &&
      !currentPerMeasure
    ) {
      return;
    }

    await downloadSimulationWorkbook({
      simulationType: "RENOVATION_COST",
      region: "FLANDERS",
      language:
        i18n.resolvedLanguage ||
        i18n.language ||
        "en",
      calculatorVersion:
        reference.priceLevel || "",
      inputs:
        currentInputs,
      results: {
        budget:
          currentBudget,
        perMeasure:
          currentPerMeasure,
      },
    });
  };

  const handleApplyWorkbook = async (
    file,
  ) => {
    const { inputs } =
      await readSimulationWorkbook(
        file,
        {
          expectedSimulationType:
            "RENOVATION_COST",
          expectedRegion:
            "FLANDERS",
        },
      );

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "floorArea",
      )
    ) {
      setFloorArea(
        asText(
          inputs.floorArea,
        ),
      );
    }

    if (
      BUILDING_TYPES.includes(
        inputs.buildingType,
      )
    ) {
      setBuildingType(
        inputs.buildingType,
      );
    }

    if (
      STOREYS.includes(
        Number(inputs.storeys),
      )
    ) {
      setStoreys(
        Number(
          inputs.storeys,
        ),
      );
    }

    const nextCurrentLabel =
      CURRENT_LABELS.includes(
        inputs.currentLabel,
      )
        ? inputs.currentLabel
        : currentLabel;

    if (
      nextCurrentLabel !==
      currentLabel
    ) {
      setCurrentLabel(
        nextCurrentLabel,
      );
    }

    const allowedTargets =
      targetLabelsFor(
        nextCurrentLabel,
      );

    if (
      allowedTargets.includes(
        inputs.targetLabel,
      )
    ) {
      setTargetLabel(
        inputs.targetLabel,
      );
    } else if (
      !allowedTargets.includes(
        targetLabel,
      )
    ) {
      setTargetLabel(
        allowedTargets[
          allowedTargets.length - 1
        ],
      );
    }

    if (
      Array.isArray(
        inputs.measures,
      )
    ) {
      setMeasures(
        inputs.measures.filter(
          (value) =>
            ENERGY_MEASURES.includes(
              value,
            ),
        ),
      );
    }

    if (
      Array.isArray(
        inputs.nonEnergyWorks,
      )
    ) {
      setNonEnergyWorks(
        inputs.nonEnergyWorks.filter(
          (value) =>
            NON_ENERGY_WORKS.includes(
              value,
            ),
        ),
      );
    }
  };

  return (
    <div className="simulator">
      {/* ---------- LEFT COLUMN ---------- */}
      <div className="simulator__left-col">
        <SimulatorHeader eyebrow={t("eyebrow")} title={t("title")} subtitle={t("subtitle")} />

        <ReuseCard
          title={t("reuse-title")}
          description={t("reuse-desc")}
          browseLabel={t("browse")}
          applyLabel={t("upload-apply")}
          onApply={handleApplyWorkbook}
        />

        <div className="simulator__card simulator__form-card">
          {/* Your house */}
          <div className="simulator__section-header">
            <div className="simulator__section-title">
              <Home size={16} />
              <span>{t("your-house")}</span>
            </div>
            <button type="button" className="simulator__btn-outline simulator__btn-outline--sm">
              <Bookmark size={14} /> {t("load-saved")}
            </button>
          </div>

          <div className="simulator__grid">
            <div className="simulator__field simulator__field--wide">
              <FieldLabel tip={t("tips.floor-area")}>{t("floor-area")}</FieldLabel>
              <FieldWithIcon icon={Ruler} unit="m²">
                <input
                  type="number"
                  min="0"
                  value={floorArea}
                  onChange={(e) => setFloorArea(e.target.value)}
                  placeholder="0"
                />
              </FieldWithIcon>
            </div>

            <div className="simulator__field">
              <FieldLabel tip={t("tips.building-type")}>{t("building-type")}</FieldLabel>
              <FieldWithIcon icon={Building2}>
                <select value={buildingType} onChange={(e) => setBuildingType(e.target.value)}>
                  {BUILDING_TYPES.map((type) => (
                    <option key={type} value={type}>{t(`building-types.${type}`)}</option>
                  ))}
                </select>
              </FieldWithIcon>
            </div>

            <div className="simulator__field">
              <FieldLabel tip={t("tips.storeys")}>{t("storeys")}</FieldLabel>
              <FieldWithIcon icon={Layers}>
                <select value={storeys} onChange={(e) => setStoreys(Number(e.target.value))}>
                  {STOREYS.map((n) => (
                    <option key={n} value={n}>{n === 3 ? t("storeys-3-plus") : n}</option>
                  ))}
                </select>
              </FieldWithIcon>
            </div>
          </div>

          {/* Part 1 */}
          <div className="simulator__section-header">
            <div className="simulator__section-title">
              <Target size={16} />
              <span>{t("part1-title")}</span>
            </div>
          </div>

          <div className="simulator__grid">
            <div className="simulator__field">
              <FieldLabel tip={t("tips.current-label")}>{t("current-label")}</FieldLabel>
              <FieldWithIcon icon={Gauge}>
                <select value={currentLabel} onChange={(e) => changeCurrentLabel(e.target.value)}>
                  {CURRENT_LABELS.map((label) => (
                    <option key={label} value={label}>{label}</option>
                  ))}
                </select>
              </FieldWithIcon>
            </div>

            <div className="simulator__field">
              <FieldLabel tip={t("tips.target-label")}>{t("target-label")}</FieldLabel>
              <FieldWithIcon icon={Target}>
                <select value={targetLabel} onChange={(e) => setTargetLabel(e.target.value)}>
                  {targetLabelsFor(currentLabel).map((label) => (
                    <option key={label} value={label}>{label}</option>
                  ))}
                </select>
              </FieldWithIcon>
            </div>
          </div>

          {/* Part 2 */}
          <div className="simulator__section-header">
            <div className="simulator__section-title">
              <Hammer size={16} />
              <span>{t("part2-title")}</span>
            </div>
          </div>
          <p className="simulator__hint">{t("part2-hint")}</p>

          <div className="simulator__field ren-cost__group">
            <FieldLabel tip={t("tips.energy-measures")}>{t("energy-measures")}</FieldLabel>
            <MeasureChips
              options={ENERGY_MEASURES}
              selected={measures}
              onToggle={toggle(setMeasures)}
              t={t}
            />
          </div>

          <div className="simulator__field ren-cost__group">
            <FieldLabel tip={t("tips.non-energy-works")}>{t("non-energy-works")}</FieldLabel>
            <MeasureChips
              options={NON_ENERGY_WORKS}
              selected={nonEnergyWorks}
              onToggle={toggle(setNonEnergyWorks)}
              t={t}
            />
          </div>

          <div className="simulator__footer">
            <button type="button" className="simulator__btn-back" onClick={onBack}>
              <ArrowLeft size={16} /> {t("back")}
            </button>
            <button type="button" className="simulator__btn-continue" onClick={onContinue}>
              {t("save-continue")} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ---------- RESULT ---------- */}
      <div className="simulator__card simulator__result-card">
        <div className="simulator__tags">
          <span className="simulator__tag">{t("tag-flanders")}</span>
          <span className="simulator__tag">{t("tag-labels", { from: currentLabel, to: targetLabel })}</span>
          <span className="simulator__tag simulator__tag--badge">{t("tag-indicative")}</span>
        </div>

        {!referenceReady ? (
          <p className="simulator__note">{t("reference-missing")}</p>
        ) : (
          <>
            {/* Part 1 */}
            <label className="simulator__result-label">
              {t("budget-label", { label: targetLabel })}
            </label>
            <span className="simulator__result-value">
              {budget ? wholeMoney(budget.budget.typical) : "— €"}
            </span>

            {!budget ? (
              <p className="simulator__empty-result">{t("empty-result")}</p>
            ) : (
              <>
                <div className="simulator__mini-cards">
                  <div className="simulator__mini-card">
                    <span className="simulator__mini-label">{t("range")}</span>
                    <span className="simulator__mini-value">{range(budget.budget)}</span>
                  </div>
                  <div className="simulator__mini-card">
                    <span className="simulator__mini-label">{t("epc-improvement")}</span>
                    <span className="simulator__mini-value">
                      {t("kwh-value", { value: number(budget.epcImprovement) })}
                    </span>
                  </div>
                </div>

                <div className="simulator__rows">
                  <div className="simulator__row">
                    <span>{t("current-epc", { label: budget.currentLabel })}</span>
                    <span>{t("kwh-value", { value: number(budget.currentValue) })}</span>
                  </div>
                  <div className="simulator__row">
                    <span>{t("target-epc", { label: budget.targetLabel })}</span>
                    <span>{t("kwh-value", { value: number(budget.targetValue) })}</span>
                  </div>
                </div>

                {budget.lessReliable && <p className="simulator__note">{t("less-reliable-label")}</p>}
              </>
            )}

            <hr className="simulator__divider" />

            {/* Part 2 */}
            <label className="simulator__result-label">{t("per-measure-label")}</label>
            <span className="simulator__result-value">
              {perMeasure ? wholeMoney(perMeasure.total.typical) : "— €"}
            </span>

            {!perMeasure ? (
              <p className="simulator__empty-result">{t("empty-measures")}</p>
            ) : (
              <>
                <p className="ren-cost__total-range">{range(perMeasure.total)}</p>

                {perMeasure.energy.length > 0 && (
                  <div className="simulator__taxes-box">
                    <span className="simulator__taxes-title">{t("energy-measures")}</span>
                    {perMeasure.energy.map((row) => (
                      <MeasureRow key={row.measure} row={row} range={range} wholeMoney={wholeMoney} t={t} />
                    ))}
                    <div className="simulator__taxes-row simulator__taxes-row--total">
                      <span>{t("total-energy")}</span>
                      <span>{wholeMoney(perMeasure.energyTotal.typical)}</span>
                    </div>
                  </div>
                )}

                {perMeasure.nonEnergy.length > 0 && (
                  <div className="simulator__taxes-box">
                    <span className="simulator__taxes-title">{t("non-energy-works")}</span>
                    {perMeasure.nonEnergy.map((row) => (
                      <MeasureRow key={row.measure} row={row} range={range} wholeMoney={wholeMoney} t={t} />
                    ))}
                    <div className="simulator__taxes-row simulator__taxes-row--total">
                      <span>{t("total-non-energy")}</span>
                      <span>{wholeMoney(perMeasure.nonEnergyTotal.typical)}</span>
                    </div>
                    <p className="ren-cost__box-note">{t("non-energy-note")}</p>
                  </div>
                )}

                {perMeasure.smallFootprint && (
                  <p className="simulator__note">{t("small-footprint-note")}</p>
                )}
                <p className="simulator__muted">{t("independent-note")}</p>
              </>
            )}

            {isFloorAreaOutsidePilotRange(floorArea) && (
              <p className="simulator__note">{t("floor-area-note")}</p>
            )}
          </>
        )}

        <p className="simulator__disclaimer">
          {t("disclaimer")}
          {reference.priceLevel && (
            <>
              <br />
              {t("price-level", { level: reference.priceLevel })}
            </>
          )}
        </p>

        <button
          type="button"
          className="simulator__btn-download"
          onClick={handleDownload}
          disabled={
            !referenceReady ||
            !hasDownloadableResult
          }
        >
          <Download size={16} /> {t("download")}
        </button>
      </div>
    </div>
  );
}
